import { VIEW_MODES } from '@wix/wix-vod-constants/dist/common/view-modes';
import { DEFAULT_LOCALE, AVAILABLE_LOCALES } from 'locale/config';
import DEVICE_TYPES from 'shared/constants/device-types';
import { invokeSafely } from './attempt';
import { hosts } from '@wix/wix-vod-shared/dist/src/common/settings/settings';
import { xor as xorGuids } from '@wix/wix-vod-shared/dist/src/common/utils/guid';
import getResizedImageUrl from '@wix/wix-vod-shared/dist/src/common/utils/get-resized-image-url';
import isDefaultADIChannel from '@wix/wix-vod-shared/dist/src/common/utils/is-default-adi-channel';
import getChannelIdFromServerCached from './get-channel-id-from-server';
import { sentryClient } from 'shared/sentry/client';
import { getRandomGuid4 } from './get-random-guid4';
import { getChannelId } from 'widget/utils/app-settings';
import { hydratedData } from 'services/hydrated-data';

const WixSDKOriginal = window.Wix;

function memoizeFirstResult(fn) {
  let result;

  return function() {
    if (!result) {
      result = fn.apply(this, arguments);
    }
    return result;
  };
}

function setChannelIdToPublicStore(channelId) {
  /* eslint-disable */
  WixSDK.Data.Public.set('channelId', channelId, { scope: 'COMPONENT' })
    .then(
      _.partial(console.log, 'Channel id has been saved to Wix public storage'),
    )
    .catch(
      _.partial(console.warn, 'Can not save channel id to Wix public storage'),
    );
}

const WixSDK = WixSDKOriginal
  ? {
      ...WixSDKOriginal,

      addEventListener: _.get(WixSDKOriginal, 'addEventListener', _.noop),
      removeEventListener: WixSDKOriginal.removeEventListener || _.noop,
      closeWindow: WixSDKOriginal.closeWindow || _.noop,

      currentMember() {
        if (WixSDKOriginal.Utils.getViewMode() === VIEW_MODES.SITE) {
          const hydratedCurrentSiteUser = hydratedData.getCurrentSiteUser();

          if (hydratedCurrentSiteUser) {
            return Promise.resolve(hydratedCurrentSiteUser);
          }

          return new Promise(WixSDKOriginal.currentMember);
        }

        return Promise.resolve(null);
      },

      getCtToken() {
        return new Promise(WixSDKOriginal.getCtToken).then(ctToken => {
          if (!ctToken) {
            return Promise.reject(null);
          }

          return ctToken;
        });
      },

      getBoundingRectAndOffsets() {
        return new Promise(WixSDKOriginal.getBoundingRectAndOffsets);
      },

      getSiteUrl() {
        const viewMode = WixSDKOriginal.Utils.getViewMode();

        return new Promise(resolve => {
          if (
            viewMode === VIEW_MODES.STANDALONE ||
            viewMode === VIEW_MODES.DASHBOARD
          ) {
            return WixSDKOriginal.Dashboard.getSiteViewUrl(siteViewUrl =>
              resolve(siteViewUrl.base),
            );
          }

          WixSDKOriginal.Settings.getSiteInfo(siteInfo =>
            resolve(siteInfo.baseUrl),
          );
        });
      },

      getCurrentPageId() {
        return new Promise(WixSDKOriginal.getCurrentPageId);
      },

      getComponentInfo() {
        return new Promise(WixSDKOriginal.getComponentInfo);
      },

      navigateToCurrentComponent() {
        WixSDKOriginal.navigateToComponent(WixSDKOriginal.Utils.getCompId());
      },

      resizeComponent({ width = 0, height = 0 }) {
        // Either width or height is mandatory
        if (width > 0 || height > 0) {
          WixSDKOriginal.resizeComponent({ width, height });
        }
      },

      Activities: {
        ...WixSDKOriginal.Activities,
        getUserSessionToken() {
          return new Promise(WixSDKOriginal.Activities.getUserSessionToken);
        },
      },

      Dashboard: {
        ...WixSDKOriginal.Dashboard,
        getEditorUrl() {
          const getEditorUrl = _.get(WixSDKOriginal, 'Dashboard.getEditorUrl');
          if (getEditorUrl) {
            return new Promise(getEditorUrl);
          }

          return Promise.resolve(null);
        },
      },

      Events: {
        ...WixSDKOriginal.Events,
      },

      Data: {
        ...WixSDKOriginal.Data,

        Public: {
          ..._.get(WixSDKOriginal, 'Data.Public', {}),

          get(key, options) {
            return new Promise(
              _.partial(WixSDKOriginal.Data.Public.get, key, options),
            );
          },

          set(key, value, options) {
            return new Promise(
              _.partial(WixSDKOriginal.Data.Public.set, key, value, options),
            );
          },

          remove(key, options) {
            return new Promise(
              _.partial(WixSDKOriginal.Data.Public.remove, key, options),
            );
          },
        },
      },

      PubSub: {
        ...WixSDKOriginal.PubSub,
        subscribe: _.get(WixSDKOriginal, 'PubSub.subscribe', _.noop),
        unsubscribe: _.get(WixSDKOriginal, 'PubSub.unsubscribe', _.noop),
      },

      Settings: {
        ...WixSDKOriginal.Settings,

        setExternalId(id) {
          return new Promise((resolve, reject) =>
            WixSDKOriginal.Settings.setExternalId(id, resolve, reject),
          );
        },

        getSiteInfo() {
          const viewMode = WixSDKOriginal.Utils.getViewMode();

          if (
            viewMode === VIEW_MODES.STANDALONE ||
            viewMode === VIEW_MODES.DASHBOARD
          ) {
            return Promise.resolve({
              url: window.location.href,
            });
          }

          return new Promise(resolve => {
            if (window.__SITE_URL__) {
              resolve({ url: window.__SITE_URL__ });
            } else {
              WixSDKOriginal.Settings.getSiteInfo(resolve);
            }
          });
        },

        getDashboardAppUrl() {
          return new Promise(WixSDKOriginal.Settings.getDashboardAppUrl);
        },
      },

      Styles: {
        ...WixSDKOriginal.Styles,

        setWidgetChannel() {
          return _.noop();
        },

        _getInitialChannelId() {
          const originInstanceId = WixSDK.Utils.getInstanceValue(
            'originInstanceId',
          );

          // handle case with multiple categories in ADI
          // for now they are taken from deeplinks service
          if (originInstanceId) {
            return getChannelIdFromServerCached({
              instanceId: originInstanceId,
            });
          }

          return WixSDK.Data.Public.get('channelId', {
            scope: 'COMPONENT',
          }).then(({ channelId }) => channelId);
        },

        /**
         * This is tmp workaround to allow retrieve channel id in ADI while switching between different design presets
         * @param params
         * @returns {*}
         * @private
         */
        extendStylesWithInitialChannelId(params) {
          const appSettingsChannelId = getChannelId(params);

          if (!isDefaultADIChannel(appSettingsChannelId)) {
            return Promise.resolve(params);
          }

          return this._getInitialChannelId()
            .then(channelId => {
              return {
                ...params,
                fonts: {
                  ...params.fonts,
                  channelId: channelId || appSettingsChannelId,
                },
              };
            })
            .catch(() => params);
        },

        getStyleParams() {
          return new Promise(WixSDKOriginal.Styles.getStyleParams).then(
            params => WixSDK.Styles.extendStylesWithInitialChannelId(params),
          );
        },

        setFontParam(key, value) {
          if (key === 'channelId' && value) {
            setChannelIdToPublicStore(value);
          }
          return new Promise(
            _.partial(WixSDKOriginal.Styles.setFontParam, key, { value }),
          );
        },

        setBooleanParam(key, value) {
          return new Promise(
            _.partial(WixSDKOriginal.Styles.setBooleanParam, key, { value }),
          );
        },

        setStyleParams(params) {
          const channelId = getChannelId(params);
          if (channelId) {
            setChannelIdToPublicStore(channelId);
          }
          return new Promise(
            _.partial(WixSDKOriginal.Styles.setStyleParams, params),
          );
        },
      },

      Utils: {
        ...WixSDKOriginal.Utils,

        getCompId: _.get(WixSDKOriginal, 'Utils.getCompId', _.noop),
        getOrigCompId: _.get(WixSDKOriginal, 'Utils.getOrigCompId', _.noop),
        getDeviceType: _.get(
          WixSDKOriginal,
          'Utils.getDeviceType',
          _.constant(DEVICE_TYPES.DESKTOP),
        ),

        getBiToken: memoizeFirstResult(function getBiToken() {
          const biToken = WixSDKOriginal.Utils.getInstanceValue('biToken');

          if (biToken) {
            return biToken;
          }

          if (window.Sentry) {
            try {
              sentryClient.captureMessage('empty-bi-token', {
                instanceId: this.getInstanceId(),
              });
            } catch (ex) {
              sentryClient.captureException(ex);
            }
          }

          return getRandomGuid4();
        }),

        getLocale() {
          // I really don't know why do we need here try/catch, but I'll leave it as it was
          try {
            const originalGetLocale = _.get(
              WixSDKOriginal,
              'Utils.getLocale',
              _.noop,
            );
            const locale = originalGetLocale();

            if (!locale || locale.length > 2) {
              // prevent xss
              return DEFAULT_LOCALE;
            }

            return _.includes(AVAILABLE_LOCALES, locale)
              ? locale
              : DEFAULT_LOCALE;
          } catch (error) {
            return DEFAULT_LOCALE;
          }
        },

        getInstance() {
          try {
            return location.search.match(/(?:\?|&)instance=([^&]+)/)[1];
          } catch (error) {
            return null;
          }
        },

        getInstanceId: memoizeFirstResult(function getInstanceId() {
          return (
            WixSDK.Utils.getInstanceValue('instanceId') || getRandomGuid4()
          );
        }),

        getInstanceValue: invokeSafely(function(key) {
          // if biToken is missing for any reason, WixSDK.Utils.getBiToken will generate fake one
          if (key === 'biToken') {
            return this.getBiToken();
          }

          if (key === 'appDefId') {
            if (WixSDK.Utils.getViewMode() === VIEW_MODES.STANDALONE) {
              return '143314e1-0660-f409-9aa7-6ed544ff1957'; // APP_DEFINITION_ID for DEV STANDALONE
            }
          }

          const result = WixSDKOriginal.Utils.getInstanceValue(key);

          if (key === 'siteOwnerId' && !result) {
            return WixSDKOriginal.Utils.getInstanceValue('uid');
          }

          return result;
        }),

        getMetaSiteIdFromBiToken() {
          let biToken;
          let instanceId;
          try {
            biToken = this.getInstanceValue('biToken');
            instanceId = this.getInstanceId();
          } catch (error) {
            // do nothing
          }

          if (biToken && instanceId) {
            return xorGuids(biToken, instanceId);
          }
        },

        getMetaSiteId() {
          const metaSiteId = this.getMetaSiteIdFromBiToken();

          if (metaSiteId) {
            return Promise.resolve(metaSiteId);
          }

          const viewMode = WixSDK.Utils.getViewMode();

          if (viewMode === VIEW_MODES.STANDALONE) {
            return Promise.reject(
              'Unable to get metaSiteId in standalone view mode',
            );
          }

          if (viewMode === VIEW_MODES.EDITOR) {
            return WixSDK.Settings.getDashboardAppUrl().then(
              url => url.match(/\/sites\/([^/]+)/)[1],
            );
          }

          return WixSDK.Dashboard.getEditorUrl().then(
            url => url.match(/metaSiteId=([^&]+)/)[1],
          );
        },

        getUid() {
          try {
            return WixSDKOriginal.Utils.getUid();
          } catch (error) {}

          try {
            return document.cookie.match(/wixClient=([^;]+)/)[1].split('|')[6];
          } catch (error) {}

          return null;
        },

        getViewMode: _.get(WixSDKOriginal, 'Utils.getViewMode', _.noop),

        isEditor() {
          return WixSDK.Utils.getViewMode() === VIEW_MODES.EDITOR;
        },

        Media: {
          getImageUrl(relativeUrl) {
            return `${hosts.image}/${relativeUrl}`;
          },

          getResizedImageUrl: (url, width, height = width, params = {}, mode) =>
            getResizedImageUrl({
              url,
              width,
              height,
              params,
              mode,
              imageHost: hosts.image,
            }),
        },
      },
    }
  : null;

export default WixSDK;
